import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { fadeUp, fadeUpChild, stagger, viewportOnce } from "./anim";
import { experience, projects } from "@/data/resume";

export const Experience = () => {
  return (
    <section
      id="work"
      data-testid="experience"
      className="relative mx-auto max-w-[1500px] px-6 py-24 md:px-12 md:py-36"
    >
      <motion.div
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mb-16 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
      >
        <div>
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-[#9A7526]">
            (04) — Experience
          </span>
          <h2 className="mt-6 font-display text-4xl font-light tracking-tight text-[#1A1A18] md:text-6xl">
            Sixteen years, seven chapters.
          </h2>
        </div>
        <p className="max-w-sm font-body text-sm font-light leading-relaxed text-[#6E6D66]">
          From engineering banking systems to leading enterprise CS teams —
          each role compounding into the next.
        </p>
      </motion.div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
        className="border-t border-black/10"
      >
        {experience.map((job, idx) => (
          <motion.article
            key={`${job.company}-${idx}`}
            variants={fadeUpChild}
            data-testid={`experience-${idx}`}
            className="group grid grid-cols-1 gap-6 border-b border-black/10 py-10 transition-colors duration-500 hover:bg-black/[0.02] md:grid-cols-12 md:gap-8 md:py-12"
          >
            <div className="md:col-span-3">
              <div className="flex items-center gap-3">
                <span className="font-display text-3xl font-light text-[#1A1A18] md:text-4xl">
                  {job.company}
                </span>
                <ArrowUpRight
                  size={18}
                  className="text-[#9A7526] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                />
              </div>
              <div className="mt-2 font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#6E6D66]">
                {job.period}
              </div>
              <div className="mt-1 font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#A3A29A]">
                {job.location}
              </div>
            </div>

            <div className="md:col-span-9">
              <h3 className="font-body text-lg font-medium text-[#1A1A18] md:text-xl">
                {job.role}
              </h3>
              <ul className="mt-5 space-y-3">
                {job.points.map((p, i) => (
                  <li
                    key={i}
                    className="flex gap-3 font-body text-sm font-light leading-relaxed text-[#6E6D66] md:text-base"
                  >
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#9A7526]" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.article>
        ))}
      </motion.div>

      {/* Project portfolio */}
      <motion.div
        id="projects"
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        className="mt-24 scroll-mt-24"
      >
        <div className="mb-8 flex items-center gap-4">
          <span className="font-mono-x text-xs uppercase tracking-[0.3em] text-[#9A7526]">
            Project Portfolio
          </span>
          <span className="h-px flex-1 bg-black/10" />
        </div>

        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="grid grid-cols-1 gap-px overflow-hidden border border-black/10 bg-black/10 md:grid-cols-2"
        >
          {projects.map((p, i) => (
            <motion.a
              key={i}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeUpChild}
              data-testid={`project-card-${i}`}
              className="group flex flex-col justify-between bg-[#FFFFFF] p-8 transition-colors duration-500 hover:bg-[#F4F2EC] md:p-10"
            >
              <div>
                <span className="font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#9A7526]">
                  {p.tag}
                </span>
                <h3 className="mt-5 flex items-center gap-2 font-display text-2xl font-light leading-snug text-[#1A1A18] md:text-3xl">
                  {p.title}
                  <ArrowUpRight
                    size={18}
                    className="text-[#9A7526] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </h3>
                <p className="mt-4 font-body text-sm font-light leading-relaxed text-[#6E6D66]">
                  {p.blurb}
                </p>
              </div>
              <span className="mt-10 font-mono-x text-[11px] uppercase tracking-[0.2em] text-[#1A1A18]">
                View project ↗
              </span>
            </motion.a>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
